/**
 * The approvals of a Business Systems Access item (5 Oct, product owner).
 *
 * Each pick names its approver from the Master approval matrix and shows
 * where it stands: waiting, approved or rejected (by whom, when, why),
 * pre-approved, or no approver named. The approver — or an admin, recorded
 * as on their behalf — gets Approve and Reject here, with a reason (required
 * to reject). The server writes the decision to ITSM first; only then does
 * it count, so a failure here means nothing was decided.
 *
 * 19 Oct (UI review, findings 3 and 4): a need the reader decides is the
 * same card as on My approvals (`ApprovalCard`), one click to approve and a
 * comment only to reject. Any other need is one line: what, one status badge
 * in a word, and who decided it, when and their comment on the line below,
 * never inside the badge.
 *
 * 29 Oct (Rusty: "too wordy; name the role, who approves, and did they yet
 * or not?"): the needs the reader does not decide are one table, a row each:
 * the role, the approver, the status with its date (a rejection's comment
 * under it). The rule behind the approver is the role's tooltip.
 *
 * On a test system (5 Oct, product owner) the test approvers may decide any
 * need instead of the matrix approver, who is still shown; the record in ITSM
 * says "TEST APPROVAL". Never in production. 28 Oct (Rusty): no banner or
 * test-approver names here; a test decision shows one small "Test" badge.
 */

import { Badge, Typography } from "@norican/react";

import { useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type { AccessApprovalNeed, AccessCase, AccessItemView, MyApproval } from "@/lib/api";

import { withLocalDates } from "../lib/access";
import { dueState, dueTone } from "../lib/my-approvals";
import { ApprovalCard } from "./approval-card";
import { useNeedStatus } from "./need-words";
import { StatusBadge } from "./status-badge";

export { useNeedDetail, useNeedStatus } from "./need-words";

/**
 * The approver's name and the rule, plus why ITSM cannot name them, if so,
 * and who may decide it instead on a test system.
 */
export function NeedRule({ need }: { need: AccessApprovalNeed }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  // 14 Oct: "until 27 Oct 2026" in the app's date format, not "until 2026-10-27".
  const rule = withLocalDates(need.why, formatDate);
  const issue =
    need.approver_issue === "not_found"
      ? t("access.approvals.notFound", { name: need.approver ?? "—" })
      : need.approver_issue === "ambiguous"
        ? t("access.approvals.ambiguous", { name: need.approver ?? "—" })
        : null;
  return (
    <>
      <Typography as="span" variant="body-sm" className="min-w-0 break-words">
        {need.approver
          ? t("access.approvals.approverLine", { name: need.approver, rule })
          : t("access.approvals.ruleLine", { rule })}
      </Typography>
      {issue ? (
        <Badge tone="warning" variant="subtle" size="xs" className="self-start">
          {issue}
        </Badge>
      ) : null}
    </>
  );
}

/** 19 Oct: a waiting need's due date, reminders sent, or "Overdue". */
export function NeedDue({ need }: { need: AccessApprovalNeed }) {
  const { t } = useT("tickets");
  const { formatRelative } = useDateFormatter();
  const state = need.state === "waiting" ? dueState(need) : null;
  if (state === null) {
    return null;
  }
  const reminders = need.reminders_sent ?? 0;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone={dueTone(state)} variant={state === "overdue" ? "solid" : "subtle"} size="xs">
        {state === "overdue"
          ? t("access.approvals.overdue")
          : t("access.approvals.due", { when: formatRelative(need.due_at ?? "") })}
      </Badge>
      {reminders > 0 ? (
        <Typography as="span" variant="caption" tone="muted">
          {t("access.approvals.reminders", { n: reminders })}
        </Typography>
      ) : null}
    </span>
  );
}

export interface ApprovalNeedsProps {
  /** The case around the item: who, start date, manager, for the decision card. */
  access: AccessCase;
  item: AccessItemView;
  /** Opened from a link to this item: its first decision takes focus. */
  focused?: boolean;
}

export function ApprovalNeeds({ access, item, focused = false }: ApprovalNeedsProps) {
  const needs = item.needs ?? [];
  const firstToDecide = needs.find((need) => need.can_decide === true)?.need_id ?? null;
  const mine = needs.filter((need) => need.can_decide === true);
  const others = needs.filter((need) => need.can_decide !== true);
  return (
    <>
      {mine.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {mine.map((need) => (
            <li key={need.need_id}>
              <ApprovalCard
                card={decisionCard(access, item, need)}
                compact
                focused={focused && need.need_id === firstToDecide}
                rejectionEmail={access.rejection_email === true}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {others.length > 0 ? <NeedTable needs={others} /> : null}
    </>
  );
}

/** "365FO – Finance", "365FO – Sales" → "Finance", "Sales" when they all share the system. */
export function roleLabels(whats: readonly string[]): string[] {
  const prefix = whats[0]?.includes(" – ") ? `${whats[0].split(" – ")[0]} – ` : null;
  return prefix !== null && whats.every((what) => what.startsWith(prefix))
    ? whats.map((what) => what.slice(prefix.length))
    : [...whats];
}

/** The needs the reader does not decide: role, approver, status (and when), a row each. */
function NeedTable({ needs }: { needs: AccessApprovalNeed[] }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const status = useNeedStatus();
  const roles = roleLabels(needs.map((need) => need.what));
  return (
    <div className="border-border overflow-x-auto rounded-md border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-bg-subtle text-start">
            <th scope="col" className="p-2 text-start font-semibold">
              {t("access.approvals.table.role")}
            </th>
            <th scope="col" className="p-2 text-start font-semibold">
              {t("access.approvals.table.approver")}
            </th>
            <th scope="col" className="p-2 text-start font-semibold">
              {t("access.approvals.table.status")}
            </th>
          </tr>
        </thead>
        <tbody>
          {needs.map((need, index) => {
            const decided =
              need.state === "approved" || need.state === "rejected"
                ? [
                    need.decided_by && need.decided_by !== need.approver ? need.decided_by : null,
                    need.decided_at ? formatDate(new Date(need.decided_at)) : null,
                  ]
                    .filter(Boolean)
                    .join(", ")
                : "";
            return (
              <tr key={need.need_id} className="border-border border-t align-top">
                <th scope="row" className="p-2 text-start font-medium" title={need.why}>
                  {roles[index]}
                </th>
                <td className="p-2">
                  {need.approver ?? "—"}
                  {need.approver_issue === "not_found" || need.approver_issue === "ambiguous" ? (
                    <Badge tone="warning" variant="subtle" size="xs" className="ms-2">
                      {t(
                        `access.approvals.${need.approver_issue === "not_found" ? "notFound" : "ambiguous"}`,
                        {
                          name: need.approver ?? "—",
                        },
                      )}
                    </Badge>
                  ) : null}
                </td>
                <td className="p-2">
                  <span className="flex flex-wrap items-center gap-2">
                    <StatusBadge domain="need" status={need.state}>
                      {status(need)}
                    </StatusBadge>
                    {decided ? (
                      <Typography as="span" variant="caption" tone="muted">
                        {decided}
                      </Typography>
                    ) : null}
                    <NeedDue need={need} />
                    {need.test_approval ? (
                      <Badge tone="neutral" variant="subtle" size="xs">
                        {t("access.completed.test.badge")}
                      </Badge>
                    ) : null}
                  </span>
                  {need.state === "rejected" && need.reason ? (
                    <Typography as="span" variant="caption" className="block break-words">
                      “{need.reason}”
                    </Typography>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** A need on the case page as the card My approvals shows (the facts the case has). */
export function decisionCard(
  access: AccessCase,
  item: AccessItemView,
  need: AccessApprovalNeed,
): MyApproval {
  return {
    case_id: access.case_id,
    case_number: access.case_number,
    case_type: access.type,
    case_path: `/access/${encodeURIComponent(access.case_id)}`,
    person_name: access.person_name,
    start_date: access.start_date ?? null,
    n: item.n,
    item: item.item,
    offering: item.offering,
    action: item.action ?? null,
    need,
    manager: access.manager?.display_name ?? null,
    requested_by: access.requested_by?.display_name ?? null,
    requested_at: access.created_at ?? null,
    team: item.team ?? null,
  };
}
